import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeftRight, Brain, Check, FileText, Filter, Focus, Loader2, Plus, RefreshCw, Save, Search, Trash2 } from 'lucide-react';
import { ApiService } from '../../services/api';
import { useModal } from '../../hooks/useModal';
import { getThemeChromeButtonStyle, getThemeChromeTextStyle } from './modes/speedReadingShared';

type EditorSection = 'texts' | 'eslestirme' | 'somut-anlam' | 'takistoskop' | 'bir-bakista-gorelim' | 'multi-fokus';
type PairType = 'synonym' | 'antonym';
type PoolType = 'somut-anlam' | 'takistoskop' | 'bir-bakista-gorelim' | 'multi-fokus' | 'eslestirme';
type TextType = 'makale' | 'öykü';
type DifficultyLevel = '2. Sınıf' | '3. Sınıf' | '4. Sınıf' | '5 ve 6. Sınıflar' | '7 ve 8. Sınıflar' | 'Lise' | 'Yetişkin' | 'İlkokul' | 'Ortaokul';
type ActiveFilter = 'all' | 'active' | 'passive';

type EditorQuestion = {
  questionId?: string;
  question: string;
  options: string[];
  correctOption: number;
  focus?: string;
};

type EditorTextListItem = {
  id: string;
  sourceKey: string;
  title: string;
  category: {
    slug: string;
    name: string;
  };
  difficultyLevel: string | null;
  textType: TextType;
  content: string;
  questions: EditorQuestion[];
  metadata?: {
    tags?: string[];
    textType?: TextType;
  };
  isActive: boolean;
  wordCount: number;
  characterCount: number;
};

type EditorTextDraft = {
  id?: string;
  title: string;
  categoryName: string;
  difficultyLevel: string;
  textType: TextType;
  tags: string[];
  isActive: boolean;
  content: string;
  questions: EditorQuestion[];
};

type EditorPairListItem = {
  id: string;
  type: PairType;
  leftWord: string;
  rightWord: string;
  level: number;
  levels?: number[];
  isActive: boolean;
};

type EditorPairDraft = {
  id?: string;
  type: PairType;
  leftWord: string;
  rightWord: string;
  levels: number[];
  isActive: boolean;
};

type EditorPoolListItem = {
  id: string;
  type: PoolType;
  word: string;
  level: number;
  levels?: number[];
  isConcrete?: boolean;
  isActive: boolean;
};

type EditorPoolDraft = {
  id?: string;
  type: PoolType;
  word: string;
  levels: number[];
  isConcrete?: boolean;
  isActive: boolean;
};

const DIFFICULTY_OPTIONS: DifficultyLevel[] = ['2. Sınıf', '3. Sınıf', '4. Sınıf', 'İlkokul', 'Ortaokul', 'Lise'];
const TEXT_TYPE_OPTIONS: TextType[] = ['öykü', 'makale'];
const SECTIONS: Array<{ id: EditorSection; label: string; icon: React.ReactNode }> = [
  { id: 'texts', label: 'Metinler', icon: <FileText className="h-4 w-4" /> },
  { id: 'eslestirme', label: 'Eşleştirme', icon: <ArrowLeftRight className="h-4 w-4" /> },
  { id: 'somut-anlam', label: 'Somut Anlam', icon: <Brain className="h-4 w-4" /> },
  { id: 'takistoskop', label: 'Takistoskop', icon: <Focus className="h-4 w-4" /> },
  { id: 'bir-bakista-gorelim', label: 'Bir Bakışta Görelim', icon: <Focus className="h-4 w-4" /> },
  { id: 'multi-fokus', label: 'Multi Fokus', icon: <Focus className="h-4 w-4" /> },
];

const textTypeLabelMap: Record<TextType, string> = {
  'öykü': 'Öykü',
  'makale': 'Makale',
};

const LEVEL_OPTIONS = Array.from({ length: 13 }, (_, index) => index + 1);
const QUESTION_OPTION_LABELS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'] as const;
const ACTIVE_FILTER_OPTIONS: Array<{ value: ActiveFilter; label: string }> = [
  { value: 'all', label: 'Tümü' },
  { value: 'active', label: 'Aktif' },
  { value: 'passive', label: 'Pasif' },
];

const buildDraftQuestionId = () => `draft_q_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const createEmptyQuestionDraft = (): EditorQuestion => ({
  questionId: buildDraftQuestionId(),
  question: '',
  options: ['', ''],
  correctOption: 0,
  focus: '',
});

const ButtonSelector = <T extends string>({
  value,
  options,
  onChange,
  columns = 2,
}: {
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (value: T) => void;
  columns?: 2 | 3 | 4;
}) => (
  <div
    className={`grid gap-2 ${columns === 4 ? 'grid-cols-2 md:grid-cols-4' : columns === 3 ? 'grid-cols-2 md:grid-cols-3' : 'grid-cols-2'}`}
  >
    {options.map((option) => {
      const active = option.value === value;
      return (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          className="rounded-xl border px-4 py-3 text-sm font-semibold transition-colors"
          style={active ? { ...getThemeChromeButtonStyle(), color: '#fff', borderColor: 'transparent' } : { backgroundColor: '#fbfdfe', color: '#58707b', borderColor: '#d7e4ea' }}
        >
          {option.label}
        </button>
      );
    })}
  </div>
);

const ToggleCard: React.FC<{
  checked: boolean;
  onChange: (checked: boolean) => void;
  title: string;
  description: string;
}> = ({ checked, onChange, title, description }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className="w-full rounded-2xl border px-4 py-4 text-left transition-all duration-200"
    style={checked
      ? {
        backgroundColor: 'color-mix(in srgb, var(--theme-primary) 10%, white)',
        borderColor: 'color-mix(in srgb, var(--theme-primary) 28%, #d7e4ea)',
        boxShadow: '0 10px 24px rgba(19, 70, 87, 0.08)',
      }
      : {
        backgroundColor: '#fbfdfe',
        borderColor: '#d7e4ea',
      }}
  >
    <div className="flex items-center gap-3">
      <span
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition-colors"
        style={checked
          ? { backgroundColor: 'var(--theme-primary)', borderColor: 'var(--theme-primary)', color: '#fff' }
          : { backgroundColor: '#fff', borderColor: '#cddbe2', color: '#9ab0ba' }}
      >
        <Check className="h-3.5 w-3.5" />
      </span>
      <div className="min-w-0">
        <div className="text-sm font-bold text-[#244c5b]">{title}</div>
        <div className="mt-1 text-xs text-[#6e858f]">{description}</div>
      </div>
    </div>
  </button>
);

const normalizeSelectedLevels = (values?: number[]) => {
  const normalized = Array.from(new Set((Array.isArray(values) ? values : []).map((value) => Number(value)).filter((value) => Number.isInteger(value) && value >= 1 && value <= 13))).sort((a, b) => a - b);
  return normalized.length > 0 ? normalized : [3];
};

const normalizeFilterLevels = (values?: number[]) => {
  return Array.from(new Set((Array.isArray(values) ? values : []).map((value) => Number(value)).filter((value) => Number.isInteger(value) && value >= 1 && value <= 13))).sort((a, b) => a - b);
};

const MultiLevelSelector: React.FC<{
  values: number[];
  onChange: (values: number[]) => void;
}> = ({ values, onChange }) => {
  const normalizedValues = normalizeSelectedLevels(values);

  return (
    <div className="grid grid-cols-2 gap-2 md:grid-cols-4 xl:grid-cols-5">
      {LEVEL_OPTIONS.map((level) => {
        const active = normalizedValues.includes(level);
        return (
          <button
            key={level}
            type="button"
            onClick={() => {
              const nextValues = active
                ? normalizedValues.filter((value) => value !== level)
                : [...normalizedValues, level];
              onChange(normalizeSelectedLevels(nextValues));
            }}
            className="rounded-xl border px-3 py-3 text-sm font-semibold transition-colors"
            style={active ? { ...getThemeChromeButtonStyle(), color: '#fff', borderColor: 'transparent' } : { backgroundColor: '#fbfdfe', color: '#58707b', borderColor: '#d7e4ea' }}
          >
            Seviye {level}
          </button>
        );
      })}
    </div>
  );
};

const createEmptyTextDraft = (): EditorTextDraft => ({
  title: '',
  categoryName: '',
  difficultyLevel: '7 ve 8. Sınıflar',
  textType: 'öykü',
  tags: [],
  isActive: true,
  content: '',
  questions: Array.from({ length: 5 }, () => createEmptyQuestionDraft()),
});

const createEmptyPairDraft = (type: PairType): EditorPairDraft => ({
  type,
  leftWord: '',
  rightWord: '',
  levels: [3],
  isActive: true,
});

const createEmptyPoolDraft = (type: PoolType): EditorPoolDraft => ({
  type,
  word: '',
  levels: [3],
  isConcrete: type === 'somut-anlam' ? true : undefined,
  isActive: true,
});

const normalizeSlug = (value: string) => {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/ı/g, 'i')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

const normalizeEditorQuestion = (question: any, index: number): EditorQuestion => {
  const rawOptions = Array.isArray(question?.options)
    ? question.options
    : question?.options && typeof question.options === 'object'
      ? QUESTION_OPTION_LABELS
        .map((label) => question.options?.[label])
        .filter((option) => option !== undefined && option !== null)
      : [];

  const options = rawOptions
    .map((option: unknown) => String(option || ''))
    .slice(0, QUESTION_OPTION_LABELS.length);

  while (options.length < 2) {
    options.push('');
  }

  const rawCorrectOption = question?.correctOption;
  const normalizedCorrectOption = typeof rawCorrectOption === 'string'
    ? QUESTION_OPTION_LABELS.indexOf(rawCorrectOption.toUpperCase() as (typeof QUESTION_OPTION_LABELS)[number])
    : Number(rawCorrectOption);

  return {
    questionId: String(question?.questionId || question?.id || `editor_q_${index + 1}`),
    question: String(question?.question || ''),
    options,
    correctOption: Number.isInteger(normalizedCorrectOption) && normalizedCorrectOption >= 0 && normalizedCorrectOption < options.length
      ? normalizedCorrectOption
      : 0,
    focus: String(question?.focus || ''),
  };
};

const normalizeEditorQuestions = (questions: any[] = []) => {
  if (!Array.isArray(questions) || questions.length === 0) {
    return Array.from({ length: 5 }, () => createEmptyQuestionDraft());
  }

  return questions.map((question, index) => normalizeEditorQuestion(question, index));
};

const FilterChip: React.FC<{
  active: boolean;
  label: string;
  onClick: () => void;
}> = ({ active, label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="rounded-xl border px-3 py-2 text-xs font-semibold transition-colors"
    style={active ? { ...getThemeChromeButtonStyle(), color: '#fff', borderColor: 'transparent' } : { backgroundColor: '#fbfdfe', color: '#58707b', borderColor: '#d7e4ea' }}
  >
    {label}
  </button>
);

const SpeedReadingEditor: React.FC = () => {
  const { showConfirm, showToast } = useModal();
  const [section, setSection] = useState<EditorSection>('texts');
  const [pairType, setPairType] = useState<PairType>('synonym');
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('active');
  const [textTypeFilter, setTextTypeFilter] = useState<'all' | TextType>('all');
  const [difficultyFilter, setDifficultyFilter] = useState<'all' | DifficultyLevel>('all');
  const [levelFilter, setLevelFilter] = useState<number[]>([]);
  const [concreteFilter, setConcreteFilter] = useState<'all' | 'concrete' | 'abstract'>('all');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');

  const [textItems, setTextItems] = useState<EditorTextListItem[]>([]);
  const [selectedTextId, setSelectedTextId] = useState<string | null>(null);
  const [textDraft, setTextDraft] = useState<EditorTextDraft>(createEmptyTextDraft);

  const [pairItems, setPairItems] = useState<EditorPairListItem[]>([]);
  const [selectedPairId, setSelectedPairId] = useState<string | null>(null);
  const [pairDraft, setPairDraft] = useState<EditorPairDraft>(createEmptyPairDraft('synonym'));

  const [poolItems, setPoolItems] = useState<EditorPoolListItem[]>([]);
  const [selectedPoolId, setSelectedPoolId] = useState<string | null>(null);
  const [poolDraft, setPoolDraft] = useState<EditorPoolDraft>(createEmptyPoolDraft('takistoskop'));
  const selectedTextIdRef = useRef<string | null>(null);
  const selectedPairIdRef = useRef<string | null>(null);
  const selectedPoolIdRef = useRef<string | null>(null);

  const currentPoolType = section === 'somut-anlam' || section === 'takistoskop' || section === 'bir-bakista-gorelim' || section === 'multi-fokus'
    ? section
    : null;

  const filteredTextItems = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase('tr-TR');
    return textItems.filter((item) => {
      if (activeFilter === 'active' && !item.isActive) return false;
      if (activeFilter === 'passive' && item.isActive) return false;
      if (textTypeFilter !== 'all' && (item.metadata?.textType || item.textType) !== textTypeFilter) return false;
      if (difficultyFilter !== 'all' && item.difficultyLevel !== difficultyFilter) return false;
      if (!needle) return true;
      return [item.title, item.category?.name, item.category?.slug, item.content].some((value) => String(value || '').toLocaleLowerCase('tr-TR').includes(needle));
    });
  }, [activeFilter, difficultyFilter, search, textItems, textTypeFilter]);

  const filteredPairItems = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase('tr-TR');
    return pairItems.filter((item) => {
      if (activeFilter === 'active' && !item.isActive) return false;
      if (activeFilter === 'passive' && item.isActive) return false;
      if (levelFilter.length > 0 && !normalizeSelectedLevels(item.levels || [item.level]).some((level) => levelFilter.includes(level))) return false;
      if (!needle) return true;
      return [item.leftWord, item.rightWord].some((value) => String(value || '').toLocaleLowerCase('tr-TR').includes(needle));
    });
  }, [activeFilter, levelFilter, search, pairItems]);

  const filteredPoolItems = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase('tr-TR');
    return poolItems.filter((item) => {
      if (activeFilter === 'active' && !item.isActive) return false;
      if (activeFilter === 'passive' && item.isActive) return false;
      if (levelFilter.length > 0 && !normalizeSelectedLevels(item.levels || [item.level]).some((level) => levelFilter.includes(level))) return false;
      if (section === 'somut-anlam' && concreteFilter !== 'all') {
        if (concreteFilter === 'concrete' && !item.isConcrete) return false;
        if (concreteFilter === 'abstract' && item.isConcrete) return false;
      }
      if (!needle) return true;
      return String(item.word || '').toLocaleLowerCase('tr-TR').includes(needle);
    });
  }, [activeFilter, concreteFilter, levelFilter, search, poolItems, section]);

  const toTextDraft = (item: EditorTextListItem): EditorTextDraft => ({
    id: item.id,
    title: item.title,
    categoryName: item.category?.name || '',
    difficultyLevel: item.difficultyLevel || '7 ve 8. Sınıflar',
    textType: item.metadata?.textType || item.textType || 'öykü',
    tags: Array.isArray(item.metadata?.tags) ? item.metadata.tags : [],
    isActive: Boolean(item.isActive),
    content: item.content || '',
    questions: normalizeEditorQuestions(item.questions),
  });

  useEffect(() => {
    selectedTextIdRef.current = selectedTextId;
  }, [selectedTextId]);

  useEffect(() => {
    selectedPairIdRef.current = selectedPairId;
  }, [selectedPairId]);

  useEffect(() => {
    selectedPoolIdRef.current = selectedPoolId;
  }, [selectedPoolId]);

  useEffect(() => {
    setShowFilters(false);
    setTextTypeFilter('all');
    setDifficultyFilter('all');
    setLevelFilter([]);
    setConcreteFilter('all');
    setActiveFilter('active');
  }, [section, pairType]);

  const loadSection = useCallback(async () => {
    try {
      setLoading(true);
      setStatus('');
      const includeInactive = activeFilter !== 'active';

      if (section === 'texts') {
        const response = await ApiService.getApi(`speed-reading/editor/contents?includeInactive=${includeInactive ? 'true' : 'false'}&limit=300`);
        const items = Array.isArray(response?.items) ? response.items : [];
        setTextItems(items);
        const selected = (selectedTextIdRef.current && items.find((item: EditorTextListItem) => item.id === selectedTextIdRef.current)) || items[0];
        if (selected) {
          setSelectedTextId(selected.id);
          setTextDraft(toTextDraft(selected));
        } else {
          setSelectedTextId(null);
          setTextDraft(createEmptyTextDraft());
        }
        return;
      }

      if (section === 'eslestirme') {
        const response = await ApiService.getApi(`speed-reading/editor/word-pairs?type=${pairType}&includeInactive=${includeInactive ? 'true' : 'false'}&limit=400`);
        const items = Array.isArray(response?.items) ? response.items : [];
        setPairItems(items);
        const selected = (selectedPairIdRef.current && items.find((item: EditorPairListItem) => item.id === selectedPairIdRef.current)) || items[0];
        if (selected) {
          setSelectedPairId(selected.id);
          setPairDraft({
            id: selected.id,
            type: selected.type,
            leftWord: selected.leftWord,
            rightWord: selected.rightWord,
            levels: normalizeSelectedLevels(selected.levels || [selected.level]),
            isActive: selected.isActive,
          });
        } else {
          setSelectedPairId(null);
          setPairDraft(createEmptyPairDraft(pairType));
        }
        return;
      }

      if (currentPoolType) {
        const response = await ApiService.getApi(`speed-reading/editor/word-pools?type=${currentPoolType}&includeInactive=${includeInactive ? 'true' : 'false'}&limit=400`);
        const items = Array.isArray(response?.items) ? response.items : [];
        setPoolItems(items);
        const selected = (selectedPoolIdRef.current && items.find((item: EditorPoolListItem) => item.id === selectedPoolIdRef.current)) || items[0];
        if (selected) {
          setSelectedPoolId(selected.id);
          setPoolDraft({
            id: selected.id,
            type: selected.type,
            word: selected.word,
            levels: normalizeSelectedLevels(selected.levels || [selected.level]),
            isConcrete: selected.isConcrete,
            isActive: selected.isActive,
          });
        } else {
          setSelectedPoolId(null);
          setPoolDraft(createEmptyPoolDraft(currentPoolType));
        }
      }
    } catch (error) {
      console.error('Düzenleyici verisi alınamadı:', error);
      setStatus(error instanceof Error ? error.message : 'Veriler alınamadı.');
    } finally {
      setLoading(false);
    }
  }, [section, activeFilter, pairType, currentPoolType]);

  useEffect(() => {
    setSearch('');
    void loadSection();
  }, [loadSection]);

  const handleCreateNew = () => {
    setStatus('');
    if (section === 'texts') {
      setSelectedTextId(null);
      setTextDraft(createEmptyTextDraft());
      return;
    }
    if (section === 'eslestirme') {
      setSelectedPairId(null);
      setPairDraft(createEmptyPairDraft(pairType));
      return;
    }
    if (currentPoolType) {
      setSelectedPoolId(null);
      setPoolDraft(createEmptyPoolDraft(currentPoolType));
    }
  };

  const handleSaveText = async () => {
    try {
      setSaving(true);
      const payload = {
        title: textDraft.title.trim(),
        categoryName: textDraft.categoryName.trim(),
        categorySlug: normalizeSlug(textDraft.categoryName),
        difficultyLevel: textDraft.difficultyLevel || null,
        isActive: textDraft.isActive,
        content: textDraft.content,
        questions: textDraft.questions
          .map((question, index) => ({
            questionId: question.questionId || `editor_q${index + 1}`,
            question: question.question.trim(),
            options: question.options.map((option) => option.trim()).filter(Boolean),
            correctOption: question.correctOption,
            focus: question.focus?.trim() || undefined,
          }))
          .filter((question) => question.question && question.options.length >= 2),
        metadata: {
          textType: textDraft.textType,
          tags: textDraft.tags.filter(Boolean),
        },
      };

      const response = textDraft.id
        ? await ApiService.putApi(`speed-reading/editor/contents/${textDraft.id}`, payload)
        : await ApiService.postApi('speed-reading/editor/contents', payload);
      const saved = response?.item as EditorTextListItem;
      setStatus('Metin kaydedildi.');
      setSelectedTextId(saved.id);
      setTextDraft(toTextDraft(saved));
      await loadSection();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'Metin kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  const updateTextQuestion = (questionIndex: number, updater: (question: EditorQuestion) => EditorQuestion) => {
    setTextDraft((prev) => ({
      ...prev,
      questions: prev.questions.map((question, index) => (index === questionIndex ? updater(question) : question)),
    }));
  };

  const handleAddQuestion = () => {
    setTextDraft((prev) => ({
      ...prev,
      questions: [...prev.questions, createEmptyQuestionDraft()],
    }));
  };

  const handleDeleteQuestion = (questionIndex: number) => {
    setTextDraft((prev) => {
      const nextQuestions = prev.questions.filter((_, index) => index !== questionIndex);
      return {
        ...prev,
        questions: nextQuestions.length > 0 ? nextQuestions : [createEmptyQuestionDraft()],
      };
    });
  };

  const handleAddQuestionOption = (questionIndex: number) => {
    updateTextQuestion(questionIndex, (question) => ({
      ...question,
      options: [...question.options, ''].slice(0, QUESTION_OPTION_LABELS.length),
    }));
  };

  const handleDeleteQuestionOption = (questionIndex: number, optionIndex: number) => {
    updateTextQuestion(questionIndex, (question) => {
      if (question.options.length <= 2) return question;

      const nextOptions = question.options.filter((_, index) => index !== optionIndex);
      let nextCorrectOption = question.correctOption;

      if (optionIndex === question.correctOption) {
        nextCorrectOption = 0;
      } else if (optionIndex < question.correctOption) {
        nextCorrectOption = Math.max(0, question.correctOption - 1);
      }

      return {
        ...question,
        options: nextOptions,
        correctOption: Math.min(nextCorrectOption, nextOptions.length - 1),
      };
    });
  };

  const handleSavePair = async () => {
    try {
      setSaving(true);
      const payload = {
        type: pairDraft.type,
        leftWord: pairDraft.leftWord.trim(),
        rightWord: pairDraft.rightWord.trim(),
        level: pairDraft.levels[0],
        levels: normalizeSelectedLevels(pairDraft.levels),
        isActive: pairDraft.isActive,
      };
      const response = pairDraft.id
        ? await ApiService.putApi(`speed-reading/editor/word-pairs/${pairDraft.id}`, payload)
        : await ApiService.postApi('speed-reading/editor/word-pairs', payload);
      const saved = response?.item as EditorPairListItem;
      setStatus(pairDraft.type === 'synonym' ? 'Eşleştirme kelime çifti kaydedildi.' : 'Zıt anlam çifti kaydedildi.');
      setSelectedPairId(saved.id);
      await loadSection();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'Kelime çifti kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePool = async () => {
    if (!currentPoolType) return;
    try {
      setSaving(true);
      const payload = {
        type: currentPoolType,
        word: poolDraft.word.trim(),
        level: poolDraft.levels[0],
        levels: normalizeSelectedLevels(poolDraft.levels),
        isConcrete: currentPoolType === 'somut-anlam' ? Boolean(poolDraft.isConcrete) : undefined,
        isActive: poolDraft.isActive,
      };
      const response = poolDraft.id
        ? await ApiService.putApi(`speed-reading/editor/word-pools/${poolDraft.id}`, payload)
        : await ApiService.postApi('speed-reading/editor/word-pools', payload);
      const saved = response?.item as EditorPoolListItem;
      setSelectedPoolId(saved.id);
      setStatus('Kelime havuzu kaydedildi.');
      await loadSection();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'Kelime havuzu kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteText = async (item: EditorTextListItem, event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    const confirmed = await showConfirm({
      title: 'Metni Sil',
      message: `"${item.title}" metni silinsin mi?\nBu işlem geri alınamaz.`,
      confirmText: 'Sil',
      cancelText: 'Vazgeç',
      confirmButtonVariant: 'danger',
    });
    if (!confirmed) return;

    try {
      setSaving(true);
      await ApiService.deleteApi(`speed-reading/editor/contents/${item.id}`);
      setTextItems((prev) => prev.filter((current) => current.id !== item.id));
      if (selectedTextId === item.id) {
        setSelectedTextId(null);
        setTextDraft(createEmptyTextDraft());
      }
      setStatus('Metin silindi.');
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'Metin silinemedi.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePair = async (item: EditorPairListItem, event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    const confirmed = await showConfirm({
      title: 'Kelime Çiftini Sil',
      message: `"${item.leftWord} - ${item.rightWord}" kelime çifti silinsin mi?\nBu işlem geri alınamaz.`,
      confirmText: 'Sil',
      cancelText: 'Vazgeç',
      confirmButtonVariant: 'danger',
    });
    if (!confirmed) return;

    try {
      setSaving(true);
      await ApiService.deleteApi(`speed-reading/editor/word-pairs/${item.id}`);
      setPairItems((prev) => prev.filter((current) => current.id !== item.id));
      if (selectedPairId === item.id) {
        setSelectedPairId(null);
        setPairDraft(createEmptyPairDraft(pairType));
      }
      setStatus('Kelime çifti silindi.');
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'Kelime çifti silinemedi.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePoolItem = async (item: EditorPoolListItem, event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    const confirmed = await showConfirm({
      title: 'Kaydı Sil',
      message: `"${item.word}" kaydı silinsin mi?\nBu işlem geri alınamaz.`,
      confirmText: 'Sil',
      cancelText: 'Vazgeç',
      confirmButtonVariant: 'danger',
    });
    if (!confirmed) return;

    try {
      setSaving(true);
      await ApiService.deleteApi(`speed-reading/editor/word-pools/${item.id}`);
      setPoolItems((prev) => prev.filter((current) => current.id !== item.id));
      if (selectedPoolId === item.id) {
        setSelectedPoolId(null);
        setPoolDraft(createEmptyPoolDraft(currentPoolType || 'takistoskop'));
      }
      setStatus('');
      showToast('Kelime havuzu kaydı silindi.', 'success', 3000);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'Kelime havuzu kaydı silinemedi.');
    } finally {
      setSaving(false);
    }
  };

  const currentList = section === 'texts' ? filteredTextItems : section === 'eslestirme' ? filteredPairItems : filteredPoolItems;
  const validQuestionCount = textDraft.questions.length === 5 || textDraft.questions.length === 10;
  const completedQuestionCount = textDraft.questions.filter((question) => (
    question.question.trim() && question.options.map((option) => option.trim()).filter(Boolean).length >= 2
  )).length;
  const canSaveTextDraft = validQuestionCount && (completedQuestionCount === 5 || completedQuestionCount === 10);

  return (
    <div className="bg-[#f3f5f7] p-3 sm:p-4 md:h-full md:min-h-0 lg:px-6 lg:pt-6 lg:pb-6">
      <div className="mx-auto flex max-w-[1480px] flex-col rounded-[24px] border border-[#e0e9ed] bg-[#f8fafb] p-4 shadow-sm sm:rounded-[28px] sm:p-5 md:h-full md:min-h-0">
        <div className="mb-5 flex shrink-0 flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="text-[22px] font-black sm:text-[28px]" style={getThemeChromeTextStyle()}>Metin Düzenleyici</div>
            <p className="mt-1 max-w-[640px] text-sm leading-6 text-[#6f8590]">Öğretmenler bu alandan metinleri ve egzersiz havuzlarını yönetebilir.</p>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center sm:gap-3">
            {SECTIONS.map((item) => (
              <button
                key={item.id}
                onClick={() => setSection(item.id)}
                className="min-w-0 rounded-2xl px-3 py-2.5 text-sm font-semibold sm:px-4"
                style={section === item.id ? { ...getThemeChromeButtonStyle(), color: '#fff' } : { backgroundColor: '#fff', color: '#506973', border: '1px solid #d6e3e9' }}
              >
                <span className="inline-flex items-center gap-2 whitespace-normal text-left leading-5 sm:whitespace-nowrap">{item.icon}{item.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="grid gap-4 lg:min-h-0 lg:flex-1 lg:gap-5 xl:grid-cols-[360px_minmax(0,1fr)]">
          <div className="flex min-h-0 flex-col rounded-[22px] border border-[#dce7ec] bg-white p-4 sm:rounded-[24px]">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div className="text-sm font-black text-[#214858]">
                {section === 'texts' ? 'Kayıtlı Metinler' : section === 'eslestirme' ? 'Eşleştirme Havuzu' : 'Kelime Havuzu'}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <button onClick={() => setShowFilters((prev) => !prev)} className="rounded-xl border border-[#dbe6eb] bg-white p-2 text-[#5a727d]">
                  <Filter className="h-4 w-4" />
                </button>
                <button onClick={() => void loadSection()} className="rounded-xl border border-[#dbe6eb] bg-white p-2 text-[#5a727d]"><RefreshCw className="h-4 w-4" /></button>
                <button onClick={handleCreateNew} className="rounded-xl px-3 py-2 text-xs font-semibold text-white" style={getThemeChromeButtonStyle()}>
                  <span className="inline-flex items-center gap-1"><Plus className="h-4 w-4" /> Yeni</span>
                </button>
              </div>
            </div>

            {section === 'eslestirme' ? (
              <div className="mb-3 flex gap-2">
                {([
                  { id: 'synonym', label: 'Eş Anlam Çiftleri' },
                  { id: 'antonym', label: 'Zıt Anlam Çiftleri' },
                ] as const).map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setPairType(item.id)}
                    className="rounded-xl px-3 py-2 text-xs font-semibold"
                    style={pairType === item.id ? { ...getThemeChromeButtonStyle(), color: '#fff' } : { backgroundColor: '#f7fafc', color: '#5a727d', border: '1px solid #dbe6eb' }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            ) : null}

            <div className="mb-3 flex items-center gap-2 rounded-xl border border-[#dce5ea] bg-[#fbfdfe] px-3 py-2">
              <Search className="h-4 w-4 text-[#89a1ac]" />
              <input value={search} onChange={(event) => setSearch(event.target.value)} className="w-full bg-transparent text-sm outline-none" placeholder="Listede ara..." />
            </div>

            {showFilters ? (
              <div className="mb-4 space-y-4 rounded-2xl border border-[#dbe6eb] bg-[#fbfdfe] p-4">
                <div className="space-y-2">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#7a8e98]">Durum</div>
                  <div className="flex flex-wrap gap-2">
                    {ACTIVE_FILTER_OPTIONS.map((option) => (
                      <FilterChip key={option.value} active={activeFilter === option.value} label={option.label} onClick={() => setActiveFilter(option.value)} />
                    ))}
                  </div>
                </div>

                {section === 'texts' ? (
                  <>
                    <div className="space-y-2">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#7a8e98]">Metin Türü</div>
                      <div className="flex flex-wrap gap-2">
                        <FilterChip active={textTypeFilter === 'all'} label="Tümü" onClick={() => setTextTypeFilter('all')} />
                        {TEXT_TYPE_OPTIONS.map((option) => (
                          <FilterChip key={option} active={textTypeFilter === option} label={textTypeLabelMap[option]} onClick={() => setTextTypeFilter(option)} />
                        ))}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#7a8e98]">Metin Zorluğu</div>
                      <div className="flex flex-wrap gap-2">
                        <FilterChip active={difficultyFilter === 'all'} label="Tümü" onClick={() => setDifficultyFilter('all')} />
                        {DIFFICULTY_OPTIONS.map((option) => (
                          <FilterChip key={option} active={difficultyFilter === option} label={option} onClick={() => setDifficultyFilter(option)} />
                        ))}
                      </div>
                    </div>
                  </>
                ) : null}

                {(section === 'eslestirme' || currentPoolType) ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#7a8e98]">Seviye</div>
                      <div className="text-[11px] font-semibold text-[#8aa0a9]">Birden fazla seviye seçebilirsin</div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <FilterChip active={levelFilter.length === 0} label="Tümü" onClick={() => setLevelFilter([])} />
                      {LEVEL_OPTIONS.map((option) => (
                        <FilterChip
                          key={option}
                          active={levelFilter.includes(option)}
                          label={`Seviye ${option}`}
                          onClick={() => {
                            setLevelFilter((prev) => {
                              const normalized = normalizeFilterLevels(prev);
                              return normalized.includes(option)
                                ? normalized.filter((item) => item !== option)
                                : normalizeFilterLevels([...normalized, option]);
                            });
                          }}
                        />
                      ))}
                    </div>
                  </div>
                ) : null}

                {section === 'somut-anlam' ? (
                  <div className="space-y-2">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#7a8e98]">Kelime Türü</div>
                    <div className="flex flex-wrap gap-2">
                      <FilterChip active={concreteFilter === 'all'} label="Tümü" onClick={() => setConcreteFilter('all')} />
                      <FilterChip active={concreteFilter === 'concrete'} label="Somut" onClick={() => setConcreteFilter('concrete')} />
                      <FilterChip active={concreteFilter === 'abstract'} label="Soyut" onClick={() => setConcreteFilter('abstract')} />
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            <div className="max-h-[380px] overflow-y-auto pr-1 md:min-h-0 md:flex-1 md:max-h-none">
              {loading ? (
                <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#6f8792]" /></div>
              ) : currentList.length === 0 ? (
                <div className="rounded-xl border border-dashed border-[#d7e3e8] bg-[#fbfdfe] px-4 py-6 text-sm text-[#70858e]">Bu bölümde kayıt bulunamadı.</div>
              ) : section === 'texts' ? (
	                <div className="space-y-3">
	                  {filteredTextItems.map((item) => (
	                    <div
	                      key={item.id}
	                      role="button"
	                      tabIndex={0}
	                      onClick={() => { setSelectedTextId(item.id); setTextDraft(toTextDraft(item)); }}
	                      onKeyDown={(event) => {
	                        if (event.key === 'Enter' || event.key === ' ') {
	                          event.preventDefault();
	                          setSelectedTextId(item.id);
	                          setTextDraft(toTextDraft(item));
	                        }
	                      }}
	                      className="group flex w-full cursor-pointer items-start justify-between gap-3 rounded-2xl border px-4 py-4 text-left transition-colors hover:border-[#c7dbe3] hover:bg-[#fbfdfe]"
	                      style={selectedTextId === item.id ? { borderColor: 'var(--theme-primary)', backgroundColor: '#f4fbfe' } : { borderColor: '#e1ebef', backgroundColor: '#fff' }}
	                    >
	                      <div className="min-w-0">
	                        <div className="line-clamp-2 text-sm font-bold text-[#244c5b]">{item.title}</div>
	                        <div className="mt-1 text-xs text-[#708690]">
	                          {[item.category?.name, item.difficultyLevel].filter(Boolean).join(' · ')}
	                        </div>
	                      </div>
	                      <button
	                        type="button"
	                        onClick={(event) => void handleDeleteText(item, event)}
	                        disabled={saving}
	                        className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-[#edd8d8] bg-[#fff6f6] text-[#9a4b4b] transition-all hover:bg-[#ffecec] disabled:cursor-wait disabled:opacity-60 ${selectedTextId === item.id ? 'opacity-100' : 'pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100'}`}
	                        title="Metni sil"
	                      >
	                        <Trash2 className="h-4 w-4" />
	                      </button>
	                    </div>
	                  ))}
	                </div>
	              ) : section === 'eslestirme' ? (
	                <div className="space-y-3">
	                  {filteredPairItems.map((item) => (
	                    <div
	                      key={item.id}
	                      role="button"
	                      tabIndex={0}
	                      onClick={() => { setSelectedPairId(item.id); setPairDraft({ id: item.id, type: item.type, leftWord: item.leftWord, rightWord: item.rightWord, levels: normalizeSelectedLevels(item.levels || [item.level]), isActive: item.isActive }); }}
	                      onKeyDown={(event) => {
	                        if (event.key === 'Enter' || event.key === ' ') {
	                          event.preventDefault();
	                          setSelectedPairId(item.id);
	                          setPairDraft({ id: item.id, type: item.type, leftWord: item.leftWord, rightWord: item.rightWord, levels: normalizeSelectedLevels(item.levels || [item.level]), isActive: item.isActive });
	                        }
	                      }}
	                      className="group flex w-full cursor-pointer items-start justify-between gap-3 rounded-2xl border px-4 py-4 text-left transition-colors hover:border-[#c7dbe3] hover:bg-[#fbfdfe]"
	                      style={selectedPairId === item.id ? { borderColor: 'var(--theme-primary)', backgroundColor: '#f4fbfe' } : { borderColor: '#e1ebef', backgroundColor: '#fff' }}
	                    >
	                      <div className="min-w-0">
	                        <div className="text-sm font-bold text-[#244c5b]">{item.leftWord}</div>
	                        <div className="mt-1 text-xs text-[#708690]">{item.rightWord}</div>
	                        <div className="mt-2 text-[11px] font-semibold text-[#8aa0a9]">{normalizeSelectedLevels(item.levels || [item.level]).map((level) => `Seviye ${level}`).join(', ')}</div>
	                      </div>
	                      <button
	                        type="button"
	                        onClick={(event) => void handleDeletePair(item, event)}
	                        disabled={saving}
	                        className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-[#edd8d8] bg-[#fff6f6] text-[#9a4b4b] transition-all hover:bg-[#ffecec] disabled:cursor-wait disabled:opacity-60 ${selectedPairId === item.id ? 'opacity-100' : 'pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100'}`}
	                        title="Kelime çiftini sil"
	                      >
	                        <Trash2 className="h-4 w-4" />
	                      </button>
	                    </div>
	                  ))}
	                </div>
	              ) : (
	                <div className="space-y-3">
	                  {filteredPoolItems.map((item) => (
	                    <div
	                      key={item.id}
	                      role="button"
	                      tabIndex={0}
	                      onClick={() => { setSelectedPoolId(item.id); setPoolDraft({ id: item.id, type: item.type, word: item.word, levels: normalizeSelectedLevels(item.levels || [item.level]), isConcrete: item.isConcrete, isActive: item.isActive }); }}
	                      onKeyDown={(event) => {
	                        if (event.key === 'Enter' || event.key === ' ') {
	                          event.preventDefault();
	                          setSelectedPoolId(item.id);
	                          setPoolDraft({ id: item.id, type: item.type, word: item.word, levels: normalizeSelectedLevels(item.levels || [item.level]), isConcrete: item.isConcrete, isActive: item.isActive });
	                        }
	                      }}
	                      className="group flex w-full cursor-pointer items-start justify-between gap-3 rounded-2xl border px-4 py-4 text-left transition-colors hover:border-[#c7dbe3] hover:bg-[#fbfdfe]"
	                      style={selectedPoolId === item.id ? { borderColor: 'var(--theme-primary)', backgroundColor: '#f4fbfe' } : { borderColor: '#e1ebef', backgroundColor: '#fff' }}
	                    >
	                      <div className="min-w-0">
	                        <div className="text-sm font-bold text-[#244c5b]">{item.word}</div>
	                        <div className="mt-1 text-xs text-[#708690]">{normalizeSelectedLevels(item.levels || [item.level]).map((level) => `Seviye ${level}`).join(', ')}</div>
	                      </div>
	                      <button
	                        type="button"
	                        onClick={(event) => void handleDeletePoolItem(item, event)}
	                        disabled={saving}
	                        className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-[#edd8d8] bg-[#fff6f6] text-[#9a4b4b] transition-all hover:bg-[#ffecec] disabled:cursor-wait disabled:opacity-60 ${selectedPoolId === item.id ? 'opacity-100' : 'pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100'}`}
	                        title="Kelimeyi sil"
	                      >
	                        <Trash2 className="h-4 w-4" />
	                      </button>
	                    </div>
	                  ))}
	                </div>
              )}
            </div>
          </div>

          <div className="flex min-h-0 flex-col rounded-[22px] border border-[#dce7ec] bg-white p-4 sm:rounded-[24px] sm:p-5">
            <div className="mb-4 flex shrink-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
              <div className="min-w-0">
                <div className="text-base font-black text-[#214858] sm:text-lg">
                  {section === 'texts'
                    ? (textDraft.id ? 'Metni Düzenle' : 'Yeni Metin Oluştur')
                    : section === 'eslestirme'
                      ? (pairDraft.id ? 'Kelime Çiftini Düzenle' : 'Yeni Kelime Çifti Ekle')
                      : (poolDraft.id ? 'Kelimeyi Düzenle' : 'Yeni Kelime Ekle')}
                </div>
                <div className="mt-1 max-w-[560px] text-sm leading-6 text-[#6f8590]">
                  {section === 'texts'
                    ? 'Metin, soru ve kategori ayarlarını güncelle.'
                    : section === 'eslestirme'
                      ? 'Eşleştirme modunda kullanılacak eş ve zıt anlam çiftlerini yönet.'
                      : 'Bu egzersizin kullandığı kelime havuzunu yönet.'}
                </div>
              </div>
              <div className="flex w-full items-center justify-end gap-3 sm:w-auto">
                {section === 'texts' ? (
                  <div className={`flex-1 text-right text-sm font-medium sm:min-w-[220px] ${canSaveTextDraft ? 'text-[#446857]' : 'text-[#8b6a22]'}`}>
                    {canSaveTextDraft
                      ? `${completedQuestionCount} soru hazır.`
                      : `Hazır soru: ${completedQuestionCount} / 5 veya 10`}
                  </div>
                ) : null}
                <button
                  onClick={() => void (section === 'texts' ? handleSaveText() : section === 'eslestirme' ? handleSavePair() : handleSavePool())}
                  disabled={saving || (section === 'texts' && !canSaveTextDraft)}
                  className="w-full rounded-2xl px-4 py-3 text-sm font-semibold text-white disabled:opacity-70 sm:w-auto"
                  style={getThemeChromeButtonStyle()}
                >
                  <span className="inline-flex items-center gap-2">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Kaydet</span>
                </button>
              </div>
            </div>

            {status ? (
              <div className={`mb-4 shrink-0 rounded-xl border px-4 py-3 text-sm font-medium ${status.includes('kaydedildi') || status.includes('silindi') ? 'border-[#d8eadf] bg-[#f3fbf6] text-[#446857]' : 'border-[#edd8d8] bg-[#fff6f6] text-[#8b4f4f]'}`}>
                {status}
              </div>
            ) : null}

            <div className="min-h-0 pr-0 md:flex-1 md:overflow-y-auto md:pr-2">
              {section === 'texts' ? (
                <div className="space-y-5">
                  <div className="grid gap-4 md:grid-cols-2">
                    <input value={textDraft.title} onChange={(event) => setTextDraft((prev) => ({ ...prev, title: event.target.value }))} className="rounded-xl border border-[#d7e4ea] px-4 py-3 text-sm outline-none" placeholder="Başlık" />
                    <input value={textDraft.categoryName} onChange={(event) => setTextDraft((prev) => ({ ...prev, categoryName: event.target.value }))} className="rounded-xl border border-[#d7e4ea] px-4 py-3 text-sm outline-none" placeholder="Kategori adı" />
                  </div>
                  <div className="space-y-3">
                    <div className="text-xs font-semibold uppercase tracking-[0.12em] text-[#7a8e98]">Metin Türü</div>
                    <ButtonSelector
                      value={textDraft.textType}
                      options={TEXT_TYPE_OPTIONS.map((option) => ({ value: option, label: textTypeLabelMap[option] }))}
                      onChange={(value) => setTextDraft((prev) => ({ ...prev, textType: value }))}
                    />
                  </div>
                  <div className="space-y-3">
                    <div className="text-xs font-semibold uppercase tracking-[0.12em] text-[#7a8e98]">Metin Zorluğu</div>
                    <ButtonSelector
                      value={textDraft.difficultyLevel as DifficultyLevel}
                      options={DIFFICULTY_OPTIONS.map((option) => ({ value: option, label: option }))}
                      onChange={(value) => setTextDraft((prev) => ({ ...prev, difficultyLevel: value }))}
                      columns={4}
                    />
                  </div>
                  <div className="space-y-2">
                    <input value={textDraft.tags.join(', ')} onChange={(event) => setTextDraft((prev) => ({ ...prev, tags: event.target.value.split(',').map((tag) => tag.trim()).filter(Boolean) }))} className="w-full rounded-xl border border-[#d7e4ea] px-4 py-3 text-sm outline-none" placeholder="Etiketler: tarih, roman, liman" />
                    <p className="text-xs leading-5 text-[#7a8e98]">Etiketler; metni aramada, gruplayıp ayırmada ve ileride öneri/filtre akışlarında hızlı sınıflandırma için kullanılır.</p>
                  </div>
                  <ToggleCard
                    checked={textDraft.isActive}
                    onChange={(checked) => setTextDraft((prev) => ({ ...prev, isActive: checked }))}
                    title="Aktif"
                    description="Bu metin öğrencilere ve egzersiz akışına açık olur."
                  />
                  <textarea value={textDraft.content} onChange={(event) => setTextDraft((prev) => ({ ...prev, content: event.target.value }))} className="min-h-[300px] w-full rounded-2xl border border-[#d7e4ea] px-4 py-4 text-sm leading-7 outline-none lg:min-h-[420px]" placeholder="Metin içeriği" />
                  <div className="space-y-4 rounded-[24px] border border-[#dce7ec] bg-[#fbfdfe] p-4 sm:p-5">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <div className="text-sm font-black text-[#214858]">Metne Bağlı Sorular</div>
                        <div className="mt-1 text-xs leading-5 text-[#6f8590]">
                          Soruları düzenleyin, yenisini ekleyin, gereksiz olanı silin ve doğru cevabı seçin.
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={handleAddQuestion}
                        disabled={textDraft.questions.length >= 10}
                        className="rounded-2xl px-4 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
                        style={getThemeChromeButtonStyle()}
                      >
                        <span className="inline-flex items-center gap-2"><Plus className="h-4 w-4" /> Soru Ekle</span>
                      </button>
                    </div>

                    <div className="space-y-4">
                      {textDraft.questions.map((question, questionIndex) => {
                        const correctAnswerText = question.options[question.correctOption]?.trim() || 'Seçilmedi';

                        return (
                          <div key={question.questionId || `question-${questionIndex}`} className="rounded-[22px] border border-[#dce7ec] bg-white p-4 shadow-sm">
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                              <div>
                                <div className="text-sm font-black text-[#214858]">Soru {questionIndex + 1}</div>
                                <div className="mt-1 text-xs text-[#708690]">
                                  Doğru cevap: {QUESTION_OPTION_LABELS[question.correctOption] || '-'} · {correctAnswerText}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleDeleteQuestion(questionIndex)}
                                className="inline-flex items-center gap-2 rounded-xl border border-[#edd8d8] bg-[#fff6f6] px-3 py-2 text-xs font-semibold text-[#9a4b4b]"
                              >
                                <Trash2 className="h-4 w-4" />
                                Soruyu Sil
                              </button>
                            </div>

                            <div className="mt-4 space-y-4">
                              <textarea
                                value={question.question}
                                onChange={(event) => updateTextQuestion(questionIndex, (currentQuestion) => ({ ...currentQuestion, question: event.target.value }))}
                                className="min-h-[100px] w-full rounded-2xl border border-[#d7e4ea] px-4 py-3 text-sm leading-6 outline-none"
                                placeholder="Soru metni"
                              />

                              <div className="space-y-3">
                                {question.options.map((option, optionIndex) => (
                                  <div key={`${question.questionId || questionIndex}-option-${optionIndex}`} className="grid gap-3 rounded-2xl border border-[#e1ebef] bg-[#fbfdfe] p-3 md:grid-cols-[auto_minmax(0,1fr)_auto_auto] md:items-center">
                                    <button
                                      type="button"
                                      onClick={() => updateTextQuestion(questionIndex, (currentQuestion) => ({ ...currentQuestion, correctOption: optionIndex }))}
                                      className="flex h-10 w-10 items-center justify-center rounded-full border text-sm font-black transition-colors"
                                      style={question.correctOption === optionIndex
                                        ? { ...getThemeChromeButtonStyle(), color: '#fff', borderColor: 'transparent' }
                                        : { backgroundColor: '#fff', color: '#58707b', borderColor: '#d7e4ea' }}
                                    >
                                      {QUESTION_OPTION_LABELS[optionIndex]}
                                    </button>
                                    <input
                                      value={option}
                                      onChange={(event) => updateTextQuestion(questionIndex, (currentQuestion) => ({
                                        ...currentQuestion,
                                        options: currentQuestion.options.map((currentOption, currentOptionIndex) => (
                                          currentOptionIndex === optionIndex ? event.target.value : currentOption
                                        )),
                                      }))}
                                      className="rounded-xl border border-[#d7e4ea] px-4 py-3 text-sm outline-none"
                                      placeholder={`${QUESTION_OPTION_LABELS[optionIndex]} şıkkı`}
                                    />
                                    <div className="text-xs font-semibold text-[#708690]">
                                      {question.correctOption === optionIndex ? 'Doğru cevap' : 'Şık'}
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteQuestionOption(questionIndex, optionIndex)}
                                      disabled={question.options.length <= 2}
                                      className="inline-flex items-center justify-center rounded-xl border border-[#edd8d8] bg-white px-3 py-3 text-xs font-semibold text-[#9a4b4b] disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </div>
                                ))}
                              </div>

                              <button
                                type="button"
                                onClick={() => handleAddQuestionOption(questionIndex)}
                                disabled={question.options.length >= QUESTION_OPTION_LABELS.length}
                                className="inline-flex items-center gap-2 rounded-xl border border-[#d7e4ea] bg-white px-4 py-3 text-sm font-semibold text-[#295567] disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                <Plus className="h-4 w-4" />
                                Şık Ekle
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : section === 'eslestirme' ? (
                <div className="space-y-5">
                  <div className="grid gap-4 md:grid-cols-2">
                    <input value={pairDraft.leftWord} onChange={(event) => setPairDraft((prev) => ({ ...prev, leftWord: event.target.value }))} className="rounded-xl border border-[#d7e4ea] px-4 py-3 text-sm outline-none" placeholder="Sol kelime" />
                    <input value={pairDraft.rightWord} onChange={(event) => setPairDraft((prev) => ({ ...prev, rightWord: event.target.value }))} className="rounded-xl border border-[#d7e4ea] px-4 py-3 text-sm outline-none" placeholder="Sağ kelime" />
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-xs font-semibold uppercase tracking-[0.12em] text-[#7a8e98]">Seviyeler</div>
                      <div className="text-[11px] font-semibold text-[#8aa0a9]">Birden fazla seviye seçebilirsin</div>
                    </div>
                    <MultiLevelSelector
                      values={pairDraft.levels}
                      onChange={(values) => setPairDraft((prev) => ({ ...prev, levels: values }))}
                    />
                  </div>
                  <ToggleCard
                    checked={pairDraft.isActive}
                    onChange={(checked) => setPairDraft((prev) => ({ ...prev, isActive: checked }))}
                    title="Aktif"
                    description="Bu kelime çifti egzersiz içinde kullanılabilir durumda olur."
                  />
                </div>
              ) : (
                <div className="space-y-5">
                  <div className="grid gap-4 md:grid-cols-2">
                    <input value={poolDraft.word} onChange={(event) => setPoolDraft((prev) => ({ ...prev, word: event.target.value }))} className="rounded-xl border border-[#d7e4ea] px-4 py-3 text-sm outline-none" placeholder="Kelime" />
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-xs font-semibold uppercase tracking-[0.12em] text-[#7a8e98]">Seviyeler</div>
                      <div className="text-[11px] font-semibold text-[#8aa0a9]">Kelime birden fazla seviyede görünebilir</div>
                    </div>
                    <MultiLevelSelector
                      values={poolDraft.levels}
                      onChange={(values) => setPoolDraft((prev) => ({ ...prev, levels: values }))}
                    />
                  </div>
                  {section === 'somut-anlam' ? (
                    <ToggleCard
                      checked={Boolean(poolDraft.isConcrete)}
                      onChange={(checked) => setPoolDraft((prev) => ({ ...prev, isConcrete: checked }))}
                      title="Somut Anlamlı Kelime"
                      description="İşaretliyse kelime somut, kapalıysa soyut olarak değerlendirilir."
                    />
                  ) : null}
                  <ToggleCard
                    checked={poolDraft.isActive}
                    onChange={(checked) => setPoolDraft((prev) => ({ ...prev, isActive: checked }))}
                    title="Aktif"
                    description="Bu kayıt ilgili egzersiz havuzunda görünür ve kullanılabilir olur."
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpeedReadingEditor;
